import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { PlaylistData } from "./types";
import {
  sendPutRequest,
  updatePlaylist,
  uploadVideoThumbnail,
} from "../../services/ContentServices";
import {
  PlayListCoverPicStyles,
  PositionTopRight,
  ViewCancleIconButton,
  ViewPlayListBoxStyles,
} from "./css";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSnackbar } from "notistack";

interface EditPlayListProps {
  setShowViewPlayList: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPlayList: string;
  playlistData: PlaylistData;
  setPlaylistData: React.Dispatch<React.SetStateAction<PlaylistData>>;
  onDataUpdate: () => void;
}

const EditPlaylist: React.FC<EditPlayListProps> = ({
  setShowViewPlayList,
  selectedPlayList,
  playlistData,
  setPlaylistData,
  onDataUpdate,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [initialData, setInitialData] = useState<PlaylistData | null>(null);
  const [mode, setMode] = useState<"view" | "edit">("view");

  const theme = useTheme();
  const inputStyle = {
    marginTop: theme.spacing(1),
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "1rem",
    },
  };

  useEffect(() => {
    if (playlistData.name && !initialData) {
      setInitialData(playlistData);
    }
  }, [playlistData]);

  useEffect(() => {
    if (initialData && mode != "view") {
      const isDisableChanges =
        playlistData.name.toLowerCase() === initialData.name.toLowerCase() &&
        playlistData.description.toLowerCase() ===
          initialData.description.toLowerCase() &&
        playlistData.coverPicturePreview?.toLowerCase() ===
          initialData.coverPicturePreview?.toLowerCase() &&
        playlistData.availableForVisitors === initialData.availableForVisitors;
      setSubmitting(isDisableChanges);
    }
  }, [initialData, playlistData, mode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue: string | boolean = type === "checkbox" ? checked : value;
    setPlaylistData((prevPlaylistData) => ({
      ...prevPlaylistData,
      [name]: newValue,
    }));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPlaylistData((prevPlaylistData) => ({
          ...prevPlaylistData,
          coverPicture: file,
          coverPicturePreview: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCoverPicture = () => {
    setPlaylistData((prevPlaylistData) => ({
      ...prevPlaylistData,
      coverPicture: null,
      coverPicturePreview: null,
    }));
  };

  const handleSavePlaylist = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (!playlistData.name) {
        enqueueSnackbar("Please fill in the title", { variant: "warning" });
        setSubmitting(false);
        return;
      }
      if (!playlistData.coverPicture && !playlistData.coverPicturePreview) {
        enqueueSnackbar("Thumbnail is required", { variant: "warning" });
        setSubmitting(false);
        return;
      }

      let thumbAwsUrl = playlistData.coverPicturePreview || "";
      if (playlistData.coverPicture) {
        const signUrlResponse = await uploadVideoThumbnail(
          playlistData.coverPicture.name
        );
        const signUrl = signUrlResponse.data?.data as string;
        thumbAwsUrl = signUrl.split("?")[0];
        const upload = await sendPutRequest({
          signUrl,
          file: playlistData.coverPicture,
        });
        if (upload.status !== 200) {
          enqueueSnackbar("Failed to upload thumbnail", { variant: "error" });
          setSubmitting(false);
          return;
        }
      }

      const body = {
        name: playlistData.name as string,
        description: playlistData.description as string,
        thumbnailUrl: thumbAwsUrl as string,
        availableForVisitors: playlistData.availableForVisitors as boolean,
        id: selectedPlayList as string,
      };

      const response = await updatePlaylist(body);
      if (response === 204) {
        setInitialData(null);
        onDataUpdate();
        enqueueSnackbar("Playlist updated successfully", {
          variant: "success",
        });
        setMode("view");
      } else {
        enqueueSnackbar("Failed to update playlist", { variant: "error" });
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Failed to update playlist", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box marginTop={theme.spacing(2)} sx={ViewPlayListBoxStyles}>
        <IconButton
          onClick={() => setShowViewPlayList(false)}
          sx={{ marginRight: theme.spacing(1) }}
        >
          <KeyboardBackspaceIcon />
        </IconButton>
        <Typography sx={{ flex: 1 }} variant="h6">
          Playlist Details
        </Typography>

        {mode == "view" ? (
          <span
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: "gray",
              marginRight: 20,
              cursor: "pointer",
              padding: "5px 10px",
              border: "1px solid gray",
              borderRadius: "4px",
              display: "inline-block",
              textAlign: "center",
            }}
            onClick={() => setMode("edit")}
          >
            {"Edit"}
          </span>
        ) : (
          submitting && (
            <span
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: "gray",
                marginRight: 20,
                cursor: "pointer",
                padding: "5px 10px",
                border: "1px solid gray",
                borderRadius: "4px",
                display: "inline-block",
                textAlign: "center",
              }}
              onClick={() => setMode("view")}
            >
              {"View"}
            </span>
          )
        )}
      </Box>
      <Box
        component="form"
        aria-label="Playlist Form"
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: "70vh",
          padding: theme.spacing(2),
        }}
        onSubmit={handleSavePlaylist}
      >
        <Divider sx={{ marginBottom: theme.spacing(2) }} />
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <label htmlFor="name">
            Playlist Name{" "}
            <span style={{ color: theme.palette.text.secondary }}>*</span>
          </label>
          <TextField
            id="name"
            variant="outlined"
            name="name"
            value={playlistData.name}
            onChange={handleInputChange}
            sx={inputStyle}
            disabled={mode == "view" ? true : false}
            required
            fullWidth
          />
        </Box>

        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <Typography sx={{ marginBottom: 2 }}>
            {mode == "edit" && "Upload"} Thumbnail{" "}
            <span style={{ color: theme.palette.text.secondary }}>*</span>
          </Typography>
          {playlistData.coverPicturePreview ? (
            <Box sx={PlayListCoverPicStyles}>
              <img
                src={playlistData.coverPicturePreview}
                width={"100%"}
                height={"100%"}
                style={{
                  objectFit: "cover",
                }}
              />
              {mode != "view" && (
                <IconButton
                  sx={PositionTopRight}
                  onClick={handleRemoveCoverPicture}
                >
                  <CancelIcon sx={ViewCancleIconButton} />
                </IconButton>
              )}
            </Box>
          ) : (
            <>
              <input
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                id="thumbnail-upload"
                disabled={mode == "view"}
              />
              <label htmlFor="thumbnail-upload">
                <Box
                  component="div"
                  sx={{
                    border: `1px dashed ${theme.palette.primary.main}`,
                    borderRadius: theme.shape.borderRadius,
                    padding: theme.spacing(2),
                    marginTop: theme.spacing(1),
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ color: theme.palette.text.disabled }}>
                    Choose a file
                  </Typography>
                </Box>
              </label>
            </>
          )}
        </Box>

        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <Box>
            <label htmlFor="description">Description</label>
          </Box>
          <Box>
            <TextField
              id="description"
              variant="outlined"
              name="description"
              disabled={mode == "view"}
              value={playlistData.description}
              onChange={handleInputChange}
              sx={{ ...inputStyle, scrollbarWidth: "none" }}
              required
              multiline
              rows={12}
              fullWidth
            />
          </Box>
        </Box>

        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={playlistData.availableForVisitors}
                onChange={handleInputChange}
                name="availableForVisitors"
                disabled={mode == "view"}
                color="primary"
              />
            }
            label="Available for Visitors"
          />
        </Box>

        {mode == "edit" && (
          <Box sx={{ marginTop: theme.spacing(2) }}>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              sx={{ fontSize: "1rem", borderRadius: theme.spacing(1) }}
              disabled={submitting}
            >
              Save Changes
            </Button>
            {mode == "edit" && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  if (initialData) setPlaylistData(initialData);
                }}
                sx={{
                  fontSize: "1rem",
                  borderRadius: theme.spacing(1),
                  marginLeft: theme.spacing(2),
                }}
              >
                Reset
              </Button>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default EditPlaylist;
